import { Box, Chip, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React from 'react';
import TooltipCell from 'src/components/TooltipCell/TooltipCell';
import { getDateTimeUSAFormat } from 'src/constants/helpers';
import { CompactChip } from './CompactChip.styles';
import { CONTACT_CAPTURE_TYPE_LABELS, getConversionRate } from '../../helpers';
import { ContactCapture } from '../../types';

const LEFT_ALIGNED_COLUMN = {
  align: 'left' as const,
  headerAlign: 'left' as const,
};

const CampaignCell = ({ campaign }: Pick<ContactCapture, 'campaign'>) => {
  if (!campaign) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        No campaign
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" height="100%" overflow="hidden">
      <Chip label={campaign.name} size="small" sx={{ maxWidth: 180 }} />
    </Stack>
  );
};

const NameCell = ({ record }: { record: ContactCapture }) => {
  const statusLabel = record.status === 'active'
    ? null
    : record.status === 'draft' ? 'Draft' : 'Inactive';

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
      <Box sx={{ minWidth: 0, flex: '0 1 auto' }}>
        <TooltipCell value={record.name} variant="body2" maxWidth="100%" />
      </Box>
      {statusLabel && (
        <CompactChip
          emphasized
          color="default"
          label={statusLabel}
          size="small"
          variant="outlined"
        />
      )}
      {record.status !== 'draft' && record.hasUnpublishedChanges && (
        <CompactChip
          color="warning"
          label="Has Unpublished Changes"
          size="small"
          variant="outlined"
        />
      )}
    </Stack>
  );
};

export const CONTACT_CAPTURE_LIST_COLUMNS: GridColDef<ContactCapture>[] = [
  {
    field: 'name',
    headerName: 'Name',
    ...LEFT_ALIGNED_COLUMN,
    minWidth: 270,
    flex: 1.7,
    renderCell: params => <NameCell record={params.row} />,
  },
  {
    field: 'type',
    headerName: 'Type',
    ...LEFT_ALIGNED_COLUMN,
    minWidth: 130,
    flex: 1,
    renderCell: params => CONTACT_CAPTURE_TYPE_LABELS[params.row.type],
  },
  {
    field: 'campaign',
    headerName: 'Campaign',
    ...LEFT_ALIGNED_COLUMN,
    minWidth: 180,
    flex: 1.2,
    sortable: false,
    renderCell: params => <CampaignCell campaign={params.row.campaign} />,
  },
  { field: 'views', headerName: 'Views', ...LEFT_ALIGNED_COLUMN, minWidth: 90, type: 'number', flex: 0.7, renderCell: params => params.row.views.toLocaleString() },
  { field: 'contacts', headerName: 'Contacts', ...LEFT_ALIGNED_COLUMN, minWidth: 95, type: 'number', flex: 0.7, renderCell: params => params.row.contacts.toLocaleString() },
  { field: 'conversionRate', headerName: 'Conversion rate', ...LEFT_ALIGNED_COLUMN, minWidth: 125, sortable: false, flex: 0.9, valueGetter: (_value, row) => getConversionRate(row.views, row.contacts) },
  { field: 'updatedAt', headerName: 'Last updated', ...LEFT_ALIGNED_COLUMN, minWidth: 140, flex: 1, renderCell: params => getDateTimeUSAFormat(new Date(params.row.updatedAt)) },
];

